import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useTerminal } from '../lib/store';
import { TopBar } from '../components/TopBar';
import { Empty, Pill } from '../components/ui';

interface Definition {
  key: string;
  label: string;
  help: string;
  type: string;
  group: string;
  legacy?: string;
}

const GROUP_LABELS: Record<string, string> = {
  terminal: 'Terminal',
  presencia: 'Presencia',
  produccion: 'Producción',
  calidad: 'Calidad',
  monitor: 'Monitor',
  sap: 'SAP',
};

/**
 * Configuración y bandeja de envíos.
 *
 * La bandeja es la pieza que el terminal clásico no tenía: cuando SAP rechaza
 * una emisión, alguien tiene que verlo y poder reintentarlo sin volver a hacer
 * el trabajo en planta.
 */
export function Admin() {
  const { notify } = useTerminal();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'parameters' | 'outbox'>('parameters');
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [outbox, setOutbox] = useState<Awaited<ReturnType<typeof api.outbox>> | null>(null);

  const loadParameters = useCallback(async () => {
    const res = await api.parameters();
    setDefinitions(res.definitions);
    setValues(res.values);
    setDirty(false);
  }, []);

  const loadOutbox = useCallback(async () => {
    setOutbox(await api.outbox());
  }, []);

  useEffect(() => {
    void loadParameters().catch(() => undefined);
  }, [loadParameters]);

  useEffect(() => {
    if (tab !== 'outbox') return;
    void loadOutbox().catch(() => undefined);
    const timer = setInterval(() => void loadOutbox().catch(() => undefined), 5000);
    return () => clearInterval(timer);
  }, [tab, loadOutbox]);

  const groups = useMemo(() => {
    const map = new Map<string, Definition[]>();
    for (const def of definitions) {
      const list = map.get(def.group);
      if (list) list.push(def);
      else map.set(def.group, [def]);
    }
    return [...map.entries()];
  }, [definitions]);

  const save = async () => {
    try {
      await api.saveParameters(values);
      notify({ kind: 'ok', title: 'Configuración guardada' });
      setDirty(false);
    } catch (err) {
      notify({ kind: 'error', title: 'No se pudo guardar', body: (err as Error).message });
    }
  };

  return (
    <div className="app">
      <TopBar title="Configuración del terminal" subtitle="Solo jefes de producción" onBack={() => navigate('/')} />
      <div className="content stack">
        <div className="chips">
          <button className={`chip${tab === 'parameters' ? ' chip--selected' : ''}`} onClick={() => setTab('parameters')}>
            Parámetros
          </button>
          <button className={`chip${tab === 'outbox' ? ' chip--selected' : ''}`} onClick={() => setTab('outbox')}>
            Envíos a SAP
            {outbox?.stats.failed ? ` · ${outbox.stats.failed} con error` : ''}
          </button>
          <div className="spacer" />
          <button
            className="chip"
            onClick={() =>
              void api
                .forceSync()
                .then((res) =>
                  notify({
                    kind: res.error ? 'error' : 'ok',
                    title: res.error ? 'Sincronización con errores' : 'Datos maestros actualizados',
                    body: res.error ?? undefined,
                  }),
                )
                .catch((err: Error) => notify({ kind: 'error', title: 'No se pudo sincronizar', body: err.message }))
            }
          >
            ⟳ Sincronizar con SAP ahora
          </button>
        </div>

        {tab === 'parameters' ? (
          <>
            {groups.map(([group, defs]) => (
              <section key={group} className="card stack">
                <div className="eyebrow">{GROUP_LABELS[group] ?? group}</div>
                {defs.map((def) => (
                  <div key={def.key} className="row" style={{ alignItems: 'flex-start', gap: 20 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 620 }}>{def.label}</div>
                      <div className="muted small">{def.help}</div>
                      {def.legacy ? (
                        <div className="small" style={{ color: 'var(--violet)' }}>
                          Antes: {def.legacy}
                        </div>
                      ) : null}
                    </div>
                    {def.type === 'boolean' ? (
                      <button
                        className={`chip${values[def.key] === 'true' ? ' chip--selected' : ''}`}
                        style={{ minWidth: 110 }}
                        onClick={() => {
                          setValues((v) => ({ ...v, [def.key]: v[def.key] === 'true' ? 'false' : 'true' }));
                          setDirty(true);
                        }}
                      >
                        {values[def.key] === 'true' ? 'Activado' : 'Desactivado'}
                      </button>
                    ) : (
                      <input
                        className="input"
                        style={{ width: 220 }}
                        inputMode={def.type === 'int' ? 'numeric' : 'text'}
                        value={values[def.key] ?? ''}
                        onChange={(e) => {
                          setValues((v) => ({ ...v, [def.key]: e.target.value }));
                          setDirty(true);
                        }}
                      />
                    )}
                  </div>
                ))}
              </section>
            ))}
            <div className="row">
              <div className="spacer" />
              <button className="btn" onClick={() => void loadParameters()}>
                Descartar cambios
              </button>
              <button className="btn btn--primary btn--lg" disabled={!dirty} onClick={() => void save()}>
                Guardar configuración
              </button>
            </div>
          </>
        ) : (
          <section className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {!outbox || outbox.items.length === 0 ? (
              <Empty icon="📭" title="No hay envíos registrados" body="Aquí aparecerán las emisiones y recibos enviados a SAP." />
            ) : (
              <table className="table table--zebra">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Documento</th>
                    <th>Nº en SAP</th>
                    <th>Intentos</th>
                    <th>Error</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {outbox.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Pill
                          tone={
                            item.status === 'done' ? 'ok' : item.status === 'failed' ? 'danger' : item.status === 'sending' ? 'accent' : 'warn'
                          }
                        >
                          {item.status === 'done'
                            ? 'Contabilizado'
                            : item.status === 'failed'
                              ? 'Con error'
                              : item.status === 'sending'
                                ? 'Enviando'
                                : 'En cola'}
                        </Pill>
                      </td>
                      <td>
                        {item.summary}
                        <div className="small muted">{new Date(item.createdAt).toLocaleString('es-ES')}</div>
                      </td>
                      <td className="mono">{item.sapDocNum ?? '—'}</td>
                      <td className="mono">{item.attempts}</td>
                      <td className="small" style={{ maxWidth: 380, color: 'var(--danger)' }}>
                        {item.lastError ?? ''}
                      </td>
                      <td>
                        {item.status === 'failed' ? (
                          <button
                            className="btn"
                            onClick={() =>
                              void api
                                .retryOutbox(item.id)
                                .then(() => loadOutbox())
                                .then(() => notify({ kind: 'ok', title: 'Reintento programado' }))
                                .catch((err: Error) => notify({ kind: 'error', title: 'No se pudo reintentar', body: err.message }))
                            }
                          >
                            Reintentar
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
